import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { MediaQuery, toast } from "@grandvisionhq/common";
import { usePlacePredictions, mapPlacePredictionToSuggestion } from "@grandvisionhq/locations";
import { useIntl, useFeatureToggles, useClickAndReserveFeatureConfig } from "@grandvisionhq/state";
import { useTealiumEventProperties, useDispatchTrackEvent } from "@grandvisionhq/svc-tealium/events";
import { useSessionStorage, useDebouncedEffect } from "@grandvisionhq/utils";
import { storeSearchSubmitEvent } from "../../../../data-layer/data-layer/actions.js";
import useStoreFinderUrlParams from "../../hooks/components/StoreSearchForm/hooks/use-store-finder-url-params.js";
import StoreSearchForm from "./StoreSearchForm.js";
import { useStoreFinderContext } from "../../../../context/StoreFinder/context/StoreFinder/context.js";
import usePlaceById from "../../../../hooks/api/hooks/api/use-place-by-id.js";
import useGetStoresByDistanceHook from "../../../../hooks/api/hooks/api/use-get-stores-by-distance-hook.js";
import { getAddressFromSuggestions } from "../../../../utils/utils/get-address-from-suggestions.js";
import { replaceStoreFinderUrlWithNewQueryParams } from "../../../../utils/utils/store-utils.js";
const scrollToFormHandlerId = "location-search-form";
const MIN_SEARCH_CHARACTER_COUNT = 2;
const StoreSearchFormContainer = ({
  appointmentTypeId,
  minSearchCharacterCount = MIN_SEARCH_CHARACTER_COUNT,
  storeQueryFilter,
  storeQueryForceDistance = true,
  queryResultsFilters,
  ...rest
}) => {
  const { getLabel } = useIntl();
  const {
    config,
    searchQuery,
    isPristineSearch,
    suggestions,
    isLocationSelected,
    userLocation,
    isUsingUserLocation,
    isUserLocationLoading,
    useUserLocationError,
    setIsUserLocationRequested,
    clearSearch,
    setSelectedLocation,
    setSuggestions,
    setIsPristineSearch,
    setStores,
    setSearchQuery,
    setIsStoresLoading,
    sku,
    events,
    setActiveMapInfoWindow
  } = useStoreFinderContext();
  const searchRef = React.createRef();
  const { featureConfigs } = useFeatureToggles();
  const maxSearchStoresResults = featureConfigs?.maxSearchStoresResults !== void 0 ? featureConfigs.maxSearchStoresResults : 30;
  const { withUrlParams, storeFilter } = config;
  const isMobile = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.PHONE;
  const clickAndReserveMode = useClickAndReserveFeatureConfig();
  const [isWaitingForSuggestions, setIsWaitingForSuggestions] = React.useState(false);
  const [getPlacePredictions, placePredictions] = usePlacePredictions({
    variables: {
      name: searchQuery
    },
    errorPolicy: "all",
    fetchPolicy: "no-cache",
    onError: (error) => {
      toast.failed("stores.page.search.getPlacePredictions.error");
      console.error("Failed to fetch place predictions: ", error);
    }
  });
  const [getPlaceById, placeById] = usePlaceById({
    id: ""
  });
  const useStoresByDistance = useGetStoresByDistanceHook(sku);
  const [getStoresByDistance, storesByDistance] = useStoresByDistance({
    searchGeoLocation: { lat: "", lon: "" },
    filter: storeQueryFilter,
    sku: sku ?? "",
    forceDistance: storeQueryForceDistance
  });
  const [userLocationInSessionStorage, setUserLocationInSessionStorage] = useSessionStorage(
    "grandvision_user_location",
    void 0,
    (value) => value === void 0
  );
  const labels = React.useMemo(
    () => ({
      placeholder: getLabel("stores.page.search.searchForm.placeholder"),
      noResults: getLabel("stores.page.autocomplete.noResults"),
      useMyCurrentLocation: getLabel("stores.page.search.useMyCurrentLocation"),
      myCurrentLocationPlaceholder: getLabel("stores.page.search.useMyCurrentLocation.placeholder"),
      noAvailableResultsScreenReaderHint: getLabel(
        "elements.autocomplete.noAvailableResultsScreenReaderHint"
      )
    }),
    [getLabel]
  );
  const onStoreSearchSubmitEvent = React.useCallback(
    ({ location, interaction }) => {
      const dataLayerEvent = storeSearchSubmitEvent({
        interaction,
        location,
        searchResults: storesByDistance.data?.length || null
      });
      DataLayer.pushEvent(dataLayerEvent);
    },
    [storesByDistance.data]
  );
  const { commonEventProperties, getCommonUserProperties } = useTealiumEventProperties();
  const storeSearchResultsEvent = React.useCallback(async () => {
    const commonUserProperties = await getCommonUserProperties();
    if (!commonUserProperties) return;
    return {
      id: "Event",
      Events_FunnelStoreSearch: "1",
      Store_Search_Keyword: searchQuery,
      Store_Search_ResultItemsQnt: storesByDistance.data?.length.toString() ?? "0",
      Store_Search_Type: isUsingUserLocation ? "geolocalized" : "text",
      ...commonEventProperties,
      ...commonUserProperties
    };
  }, [
    commonEventProperties,
    getCommonUserProperties,
    isUsingUserLocation,
    searchQuery,
    storesByDistance.data?.length
  ]);
  const handleOnChange = React.useCallback(
    (value) => {
      setSearchQuery(value);
      setIsWaitingForSuggestions(false);
      setSelectedLocation((prevLocation) => prevLocation === value ? prevLocation : "");
    },
    [setSearchQuery, setSelectedLocation]
  );
  const newQueryParams = React.useMemo(
    () => isUsingUserLocation ? { userLocation: "true" } : { search: searchQuery },
    [isUsingUserLocation, searchQuery]
  );
  const initialSearchQuery = useStoreFinderUrlParams({
    handleOnChange,
    setIsUserLocation: setIsUserLocationRequested,
    newQueryParams,
    enabled: withUrlParams
  });
  const getStoresByLocation = React.useCallback(
    (id) => {
      getPlaceById({ variables: { id } });
      setIsStoresLoading(true);
      const address = getAddressFromSuggestions(suggestions, id);
      setSelectedLocation(address);
      withUrlParams && replaceStoreFinderUrlWithNewQueryParams({ search: address });
      setSuggestions([]);
      if (isPristineSearch) {
        setIsPristineSearch(false);
      }
    },
    [
      getPlaceById,
      setSelectedLocation,
      setSuggestions,
      setIsStoresLoading,
      isPristineSearch,
      suggestions,
      setIsPristineSearch,
      withUrlParams
    ]
  );
  const onSuggestionClick = React.useCallback(
    (id) => {
      getStoresByLocation(id);
    },
    [getStoresByLocation]
  );
  const onChange = React.useCallback(
    (event) => handleOnChange(event.currentTarget.value),
    [handleOnChange]
  );
  const onSubmit = React.useCallback(() => {
    setActiveMapInfoWindow(null);
    if (suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
    } else {
      setIsWaitingForSuggestions(true);
    }
  }, [setActiveMapInfoWindow, getStoresByLocation, setIsWaitingForSuggestions, suggestions]);
  React.useEffect(() => {
    if (isUsingUserLocation) {
      setSearchQuery(labels.myCurrentLocationPlaceholder);
      setSelectedLocation(labels.myCurrentLocationPlaceholder);
    }
  }, [
    isUsingUserLocation,
    labels.myCurrentLocationPlaceholder,
    setSearchQuery,
    setSelectedLocation
  ]);
  const handleOnSearch = React.useCallback(() => {
    if (searchQuery.length >= minSearchCharacterCount && !isLocationSelected && !isUsingUserLocation) {
      getPlacePredictions({ variables: { name: searchQuery } });
    }
  }, [
    searchQuery,
    minSearchCharacterCount,
    isLocationSelected,
    isUsingUserLocation,
    getPlacePredictions
  ]);
  const onUseMyCurrentLocation = React.useCallback(
    (clickLocation) => {
      setIsUserLocationRequested(true);
      setIsWaitingForSuggestions(false);
      if (clickAndReserveMode) {
        events?.onClickLocationEvent?.(clickAndReserveMode, clickLocation);
      }
    },
    [setIsUserLocationRequested, setIsWaitingForSuggestions, events, clickAndReserveMode]
  );
  useDebouncedEffect(
    () => {
      handleOnSearch();
    },
    500,
    [searchQuery]
  );
  React.useEffect(() => {
    if (!placePredictions.data) {
      return;
    }
    setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
  }, [placePredictions.data]);
  React.useEffect(() => {
    if (storesByDistance.error || placeById.error) {
      setIsStoresLoading(false);
    }
  }, [storesByDistance.error, placeById.error, setIsStoresLoading]);
  React.useEffect(() => {
    if (useUserLocationError) {
      setSearchQuery("");
      setSelectedLocation("");
      setIsUserLocationRequested(false);
      onStoreSearchSubmitEvent({ location: null, interaction: "current location" });
    }
  }, [useUserLocationError, setSearchQuery, setSelectedLocation, setIsUserLocationRequested]);
  React.useEffect(() => {
    if (!placeById.data) {
      return;
    }
    const {
      coordinates: { lat, lon }
    } = placeById.data;
    const deviceGeoLocation = userLocation ? { lat: userLocation.lat, lon: userLocation.lng } : userLocationInSessionStorage && Object.keys(userLocationInSessionStorage).length !== 0 ? { lat: userLocationInSessionStorage.lat, lon: userLocationInSessionStorage.lng } : void 0;
    getStoresByDistance({
      variables: {
        query: appointmentTypeId,
        maxResults: maxSearchStoresResults,
        searchGeoLocation: {
          lat,
          lon
        },
        deviceGeoLocation,
        filter: storeQueryFilter,
        forceDistance: storeQueryForceDistance,
        sku: sku ?? ""
      }
    });
  }, [placeById.data, appointmentTypeId, storeQueryForceDistance, sku]);
  const dispatchTrackEvent = useDispatchTrackEvent();
  React.useEffect(() => {
    if (!storesByDistance.data) {
      return;
    }
    onStoreSearchSubmitEvent({
      location: storesByDistance.data.length > 0 && storesByDistance.data[0]?.town,
      interaction: isUsingUserLocation && userLocation !== void 0 ? "current location" : "location typed"
    });
    dispatchTrackEvent(storeSearchResultsEvent);
    setStores(storesByDistance.data.filter(storeFilter));
    setIsStoresLoading(false);
    searchRef.current?.blur();
    if (isMobile && storesByDistance.data.length > 0) {
      document.getElementById(scrollToFormHandlerId)?.scrollIntoView();
    }
  }, [storesByDistance.data]);
  React.useEffect(() => {
    if (searchQuery.length < minSearchCharacterCount) {
      setSuggestions([]);
    }
  }, [searchQuery, minSearchCharacterCount, setSuggestions]);
  React.useEffect(() => {
    if (!userLocation) {
      return;
    }
    if (isPristineSearch) {
      setIsPristineSearch(false);
    }
    setUserLocationInSessionStorage(userLocation);
    const { lat, lng: lon } = userLocation;
    getStoresByDistance({
      variables: {
        query: appointmentTypeId,
        maxResults: maxSearchStoresResults,
        searchGeoLocation: {
          lat,
          lon
        },
        deviceGeoLocation: {
          lat,
          lon
        },
        forceDistance: storeQueryForceDistance,
        sku: sku ?? ""
      }
    });
    setIsStoresLoading(true);
  }, [userLocation, appointmentTypeId, isPristineSearch, storeQueryForceDistance, sku]);
  React.useEffect(() => {
    if (isWaitingForSuggestions && suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
      setIsWaitingForSuggestions(false);
    }
  }, [suggestions, isWaitingForSuggestions, getStoresByLocation, setIsWaitingForSuggestions]);
  React.useEffect(() => {
    if (initialSearchQuery === searchQuery && isPristineSearch && suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
    }
  }, [initialSearchQuery, searchQuery, isPristineSearch, suggestions, getStoresByLocation]);
  React.useEffect(() => {
    if (!isUsingUserLocation) {
      return;
    }
    searchRef.current?.dispatchEvent(new KeyboardEvent("keyup", { code: "Enter" }));
    searchRef.current?.dispatchEvent(new KeyboardEvent("keyup", { code: "Space" }));
  }, [isUsingUserLocation, searchRef]);
  return /* @__PURE__ */ React.createElement(
    StoreSearchForm,
    {
      ...rest,
      searchRef,
      labels,
      loading: placePredictions.loading || isUserLocationLoading,
      showClearButton: searchQuery.length > 0,
      searchQuery,
      suggestions,
      onChange,
      onSubmit,
      onClear: clearSearch,
      onSuggestionClick,
      onSuggestionTopContentClick: () => onUseMyCurrentLocation("search suggestion"),
      onUseMyCurrentLocationClick: () => onUseMyCurrentLocation("button"),
      isSuggestionSelected: isLocationSelected,
      isFocusedOnMount: true,
      isUsingUserLocation,
      minSearchCharacterCount,
      queryResultsFilters
    }
  );
};
export {
  StoreSearchFormContainer as default
};
//# sourceMappingURL=StoreSearchFormContainer.js.map
